import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "consultant", network: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      const memberRoles = ["consultant", "staff", "agency_manager"];
      toast.success("Conta criada!");
      navigate(memberRoles.includes(form.role) ? "/app/onboarding" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { v: "consultant", label: "Consultor" },
    { v: "agency_manager", label: "Broker / Team Leader" },
    { v: "staff", label: "Staff" },
    { v: "coach", label: "Coach" },
    { v: "master", label: "Master Franchising" },
  ];

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-[#0a0b0e] items-center justify-center p-12 border-r border-brand-border overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(circle at 70% 30%, rgba(255,92,0,0.3), transparent 50%), radial-gradient(circle at 30% 70%, rgba(26,86,255,0.3), transparent 50%)" }} />
        <div className="relative max-w-md">
          <Logo className="h-12" />
          <h2 className="font-heading font-black text-3xl mt-8 leading-tight">
            Junte-se à <span className="text-brand-blue">A4G League</span>.
          </h2>
          <p className="text-brand-muted mt-4">Crie a sua conta e comece a acumular pontos rumo à excelência.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-white mb-8">
            <ArrowLeft size={16} /> Voltar
          </Link>
          <div className="lg:hidden mb-6"><Logo className="h-9" withText /></div>
          <h1 className="font-heading font-bold text-2xl">Criar conta</h1>
          <p className="text-sm text-brand-muted mt-1">Comece grátis. Sem cartão de crédito.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm">Nome</Label>
              <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)}
                required data-testid="register-name" placeholder="O seu nome"
                className="mt-1.5 bg-brand-surface border-brand-border rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                required data-testid="register-email" placeholder="nome@agencia.pt"
                className="mt-1.5 bg-brand-surface border-brand-border rounded-sm h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm">Palavra-passe</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
                required minLength={6} data-testid="register-password" placeholder="Mínimo 6 caracteres"
                className="mt-1.5 bg-brand-surface border-brand-border rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-sm">Perfil</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {roles.map((r) => (
                  <button key={r.v} type="button" onClick={() => set("role", r.v)}
                    data-testid={`register-role-${r.v}`}
                    className={`h-11 rounded-sm border text-sm font-medium transition-colors ${
                      form.role === r.v ? "border-brand-blue bg-brand-blue/15 text-white" : "border-brand-border text-brand-muted hover:text-white"
                    }`}>{r.label}</button>
                ))}
              </div>
            </div>
            {form.role === "master" && (
              <div>
                <Label htmlFor="network" className="text-sm">Nome da rede</Label>
                <Input id="network" value={form.network} onChange={(e) => set("network", e.target.value)}
                  data-testid="register-network" placeholder="Ex: Rede Prime"
                  className="mt-1.5 bg-brand-surface border-brand-border rounded-sm h-11" />
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar conta"}
            </Button>
          </form>

          <p className="text-sm text-brand-muted mt-6 text-center">
            Já tem conta?{" "}
            <Link to="/login" className="text-brand-blue font-semibold hover:underline">Inicie sessão</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
